import { useState, useCallback, useRef, useEffect } from 'react';

export interface CellChange {
  rowId: string;
  field: string;
  oldValue: unknown;
  newValue: unknown;
}

export interface UndoRedoAction {
  changes: CellChange[];
  timestamp: number;
  type: 'single' | 'batch';
}

interface UseCellUndoRedoOptions<T> {
  processRowUpdate: (updatedRow: T, oldRow?: T) => T | Promise<T>;
  getData: () => T[];
}

export const useCellUndoRedo = <T extends { id: string }>({
  processRowUpdate,
  getData,
}: UseCellUndoRedoOptions<T>) => {
  const [undoStack, setUndoStack] = useState<UndoRedoAction[]>([]);
  const [redoStack, setRedoStack] = useState<UndoRedoAction[]>([]);
  const currentBatch = useRef<CellChange[]>([]);
  const isBatching = useRef(false);
  const isUndoRedoOperation = useRef(false);

  // Auto-batching: groups rapid successive edits (e.g. paste) into a single undo action
  const autoBatchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoBatchChangesRef = useRef<CellChange[]>([]);

  // Helper function to find current row data
  const findCurrentRow = useCallback(
    (rowId: string): T | undefined => {
      return getData().find(row => String(row.id) === String(rowId));
    },
    [getData]
  );

  // Start a batch operation (for copy-paste)
  const startBatch = useCallback(() => {
    if (isUndoRedoOperation.current) return;
    isBatching.current = true;
    currentBatch.current = [];
  }, []);

  // End a batch operation and add to undo stack
  const endBatch = useCallback(() => {
    if (isUndoRedoOperation.current) return;
    if (isBatching.current && currentBatch.current.length > 0) {
      const action: UndoRedoAction = {
        changes: [...currentBatch.current],
        timestamp: Date.now(),
        type: 'batch',
      };

      setUndoStack(prev => [...prev, action]);

      // Clear redo stack when new action is performed
      setRedoStack([]);
    }
    isBatching.current = false;
    currentBatch.current = [];
  }, []);

  // Wrapped processRowUpdate that tracks changes
  const wrappedProcessRowUpdate = useCallback(
    async (updatedRow: T, oldRow?: T): Promise<T> => {
      // Skip tracking during undo/redo operations
      if (isUndoRedoOperation.current) {
        return await processRowUpdate(updatedRow, oldRow);
      }

      // Find the current row if oldRow not provided
      const currentRow = oldRow || findCurrentRow(String(updatedRow.id));

      if (!currentRow) {
        return await processRowUpdate(updatedRow, oldRow);
      }

      // Track changes between old and new row
      const changes: CellChange[] = [];

      // More efficient change detection
      for (const field in updatedRow) {
        if (field === 'id') continue; // Skip ID field

        const oldValue = currentRow[field as keyof T];
        const newValue = updatedRow[field as keyof T];

        // Only track if values are actually different (strict comparison)
        if (oldValue !== newValue) {
          changes.push({
            rowId: String(updatedRow.id),
            field,
            oldValue,
            newValue,
          });
        }
      }

      // Process the update
      const result = await processRowUpdate(updatedRow, oldRow);

      // Add changes to history if any were made
      if (changes.length > 0) {
        if (isBatching.current) {
          // Add to explicit batch (startBatch/endBatch)
          currentBatch.current.push(...changes);
        } else {
          // Auto-batch: collect rapid successive edits (e.g. paste) into one undo action.
          // A 200ms debounce window groups paste operations while keeping manual edits separate.
          autoBatchChangesRef.current.push(...changes);

          if (autoBatchTimeoutRef.current) {
            clearTimeout(autoBatchTimeoutRef.current);
          }

          autoBatchTimeoutRef.current = setTimeout(() => {
            const allChanges = [...autoBatchChangesRef.current];
            autoBatchChangesRef.current = [];
            autoBatchTimeoutRef.current = null;

            if (allChanges.length === 0) return;

            const action: UndoRedoAction = {
              changes: allChanges,
              timestamp: Date.now(),
              type: allChanges.length > 1 ? 'batch' : 'single',
            };

            setUndoStack(prev => [...prev, action]);
            setRedoStack([]);
          }, 200);
        }
      }

      return result;
    },
    [processRowUpdate, findCurrentRow]
  );

  // Undo function
  const undo = useCallback(async () => {
    if (undoStack.length === 0) return;

    const action = undoStack[undoStack.length - 1];
    isUndoRedoOperation.current = true;

    try {
      // Apply all changes in reverse order (create reversed copy to avoid mutating original)
      const reversedChanges = [...action.changes].reverse();
      for (const change of reversedChanges) {
        const currentRow = findCurrentRow(change.rowId);
        if (currentRow) {
          const updatedRow = {
            ...currentRow,
            [change.field]: change.oldValue,
          };
          await processRowUpdate(updatedRow as T, currentRow);
        }
      }

      // Move action from undo to redo stack
      setUndoStack(prev => prev.slice(0, -1));
      setRedoStack(prev => [...prev, action]);
    } finally {
      isUndoRedoOperation.current = false;
    }
  }, [undoStack, processRowUpdate, findCurrentRow]);

  // Redo function
  const redo = useCallback(async () => {
    if (redoStack.length === 0) return;

    const action = redoStack[redoStack.length - 1];
    isUndoRedoOperation.current = true;

    try {
      // Apply all changes in original order
      for (const change of action.changes) {
        const currentRow = findCurrentRow(change.rowId);
        if (currentRow) {
          const updatedRow = {
            ...currentRow,
            [change.field]: change.newValue,
          };
          await processRowUpdate(updatedRow as T, currentRow);
        }
      }

      // Move action from redo to undo stack
      setRedoStack(prev => prev.slice(0, -1));
      setUndoStack(prev => [...prev, action]);
    } finally {
      isUndoRedoOperation.current = false;
    }
  }, [redoStack, processRowUpdate, findCurrentRow]);

  // Get status information
  const canUndo = undoStack.length > 0;
  const canRedo = redoStack.length > 0;

  const getUndoActionInfo = () => {
    if (!canUndo) return null;
    const action = undoStack[undoStack.length - 1];
    return {
      type: action.type,
      changeCount: action.changes.length,
      description:
        action.type === 'batch'
          ? `Undo ${action.changes.length} cell changes`
          : 'Undo cell edit',
    };
  };

  const getRedoActionInfo = () => {
    if (!canRedo) return null;
    const action = redoStack[redoStack.length - 1];
    return {
      type: action.type,
      changeCount: action.changes.length,
      description:
        action.type === 'batch'
          ? `Redo ${action.changes.length} cell changes`
          : 'Redo cell edit',
    };
  };

  // Clean up auto-batch timeout on unmount
  useEffect(() => {
    return () => {
      if (autoBatchTimeoutRef.current) {
        clearTimeout(autoBatchTimeoutRef.current);
      }
    };
  }, []);

  // Clear history
  const clearHistory = useCallback(() => {
    setUndoStack([]);
    setRedoStack([]);
    currentBatch.current = [];
    isBatching.current = false;
    autoBatchChangesRef.current = [];
    if (autoBatchTimeoutRef.current) {
      clearTimeout(autoBatchTimeoutRef.current);
      autoBatchTimeoutRef.current = null;
    }
  }, []);

  return {
    // Main functions
    wrappedProcessRowUpdate,
    undo,
    redo,

    // Batch operations
    startBatch,
    endBatch,

    // Status
    canUndo,
    canRedo,
    getUndoActionInfo,
    getRedoActionInfo,

    // Utility
    clearHistory,

    // Debug info
    undoStackSize: undoStack.length,
    redoStackSize: redoStack.length,
  };
};
